import React, { useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import PageHeader from '../components/PageHeader';
import usePageSearchIndex from '../hooks/usePageSearchIndex';
import './Overview.css';

const Overview = () => {
  const { isMarathi } = useLanguage();

  usePageSearchIndex({
    mr: ['आढावा', 'मंडळ', 'ब्रीद', 'ध्येय', 'कार्य', 'गुणवत्ता धोरण', 'परिचय'],
    en: ['overview', 'board', 'motto', 'goal', 'functions', 'quality policy', 'introduction']
  });

  // Scroll-triggered animation for board member cards
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const boardMemberCards = document.querySelectorAll('.board-member-card');
    boardMemberCards.forEach((card) => {
      observer.observe(card);
    });

    return () => {
      boardMemberCards.forEach((card) => {
        observer.unobserve(card);
      });
    };
  }, []);

  const pageHeaderData = {
    title: {
      marathi: 'आमच्याविषयी',
      english: 'About Us'
    },
    subtitle: {
      marathi: 'महाराष्ट्र कामगार कल्याण मंडळाची माहिती',
      english: 'Information about Maharashtra Labour Welfare Board'
    }
  };

  return (
    <div className="overview">
      <div className="overview-container">
        {/* Page Header */}
        <PageHeader 
          title={pageHeaderData.title}
          subtitle={pageHeaderData.subtitle}
        />

        {/* Main Content */}
        <div className="overview-content">
          {/* Board Members Section */}
          <section className="board-members-section overview-section" data-read-section="overview-board-members">
            <div className="board-members-grid">
              {/* Board Member Card 1 */}
              <div className="board-member-card">
                <div className="board-member-photo">
                  <img 
                    src="/images/board-members-cards/board-members-card-1.JPG" 
                    alt="Board Member 1"
                    className="board-photo-image"
                  />
                </div>
                <div className="board-member-info">
                  <h3 className="board-member-name">
                    {isMarathi() ? 'मा. श्रीमती आय. ए. कुंदन' : 'Hon. Mrs. I. A. Kundan'}
                  </h3>
                  <p className="board-member-designation">
                    {isMarathi() ? 'भा. प्र. से. प्रधान सचिव (कामगार)' : 'IAS, Principal Secretary (Labour)'}
                  </p>
                </div>
              </div>

              {/* Board Member Card 2 */}
              <div className="board-member-card">
                <div className="board-member-photo">
                  <img 
                    src="/images/board-members-cards/board-members-card-2.JPG" 
                    alt="Board Member 2"
                    className="board-photo-image"
                  />
                </div>
                <div className="board-member-info">
                  <h3 className="board-member-name">
                    {isMarathi() ? 'मा. श्रीमती रोशनी कदम - पाटील' : 'Hon. Mrs. Roshni Kadam-Patil'}
                  </h3>
                  <p className="board-member-designation">
                    {isMarathi() ? 'उपसचिव (कामगार-१०), महाराष्ट्र कामगार कल्याण मंडळ, मुंबई' : 'Deputy Secretary (Labour-10), Maharashtra Labour Welfare Board, Mumbai'}
                  </p>
                </div>
              </div>

              {/* Board Member Card 3 */}
              <div className="board-member-card">
                <div className="board-member-photo">
                  <img 
                    src="/images/board-members-cards/board-members-card-3.JPG" 
                    alt="Board Member 3"
                    className="board-photo-image"
                  />
                </div>
                <div className="board-member-info">
                  <h3 className="board-member-name">
                    {isMarathi() ? 'मा. श्री. भास्कर मोरडे' : 'Hon. Shri. Bhaskar Morade'}
                  </h3>
                  <p className="board-member-designation">
                    {isMarathi() ? 'कल्याण आयुक्त, महाराष्ट्र कामगार कल्याण मंडळ' : 'Welfare Commissioner, Maharashtra Labour Welfare Board'}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Mission Section */}
          <section className="overview-section" data-read-section="overview-section">
            <h2 className="section-title">
              {isMarathi() ? 'आढावा' : 'Overview'}
            </h2>
            <div className="section-content">
              <div className="section-text">
                {isMarathi() ? (
                  <div>
                    <p style={{textAlign: 'center'}}><strong>महाराष्ट्र कामगार कल्याण मंडळ</strong></p>
                    <p style={{textAlign: 'center'}}>मा.श्री. भास्कर मोरडे</p>
                    <p style={{textAlign: 'center'}}>( कल्याण आयुक्त )</p>
                    
                    <h3>मंडळाचे ब्रीद:</h3>
                    <p>शिस्त, सेवा, सुधार आणि समृद्धी.</p>
                    
                    <h3>मंडळाचे ध्येय:</h3>
                    <p>महाराष्ट्र राज्यात समाजाच्या विविध घटकातील नोंदणीकृत कामगारांना कल्याणकारी योजना, कार्यक्रम व सुविधा पूरवून त्यांचे व त्यांच्या कुटुंबियांचे जीवनमान उंचावणे, मंडळाच्या कल्याणकारी योजनांद्वारे त्यांची सामाजिक, सांस्कृतीक, शैक्षणिक, आर्थिक वा मानसिक उन्नती घडविणे.</p>
                    
                    <h3>मंडळाचे कार्य:</h3>
                    <p>कामगार वा कामगार कुटुंबियांकरिता कल्याणकारी सेवा उपलब्ध करुन देणे.</p>
                    <ol>
                      <li>उत्तम खेळ व क्रीडा सुविधा</li>
                      <li>अद्यावत ग्रंथालय व वाचनालय</li>
                      <li>आधुनिक शैक्षणिक सुविधा</li>
                      <li>सांस्कृतिक व सामाजिक कार्यक्रमाचे आयोजन</li>
                      <li>रोजगार व स्वयंरोजगार विषयक कार्यक्रम</li>
                      <li>आरोग्य विषयक कार्यक्रम</li>
                      <li>इतर आधारित सेवा</li>
                    </ol>
                    
                    <h3>गुणवत्ता धोरण:</h3>
                    <p>कामगारांचे सर्वकष समाधान करणाऱ्या कल्याणकारी कार्यक्रमाचे नियोजनबद्ध व पद्धतशीर आयोजन करून मंडळाचे उद्दिष्ट साध्य करणे.</p>
                  </div>
                ) : (
                  <div>
                    <p style={{textAlign: 'center'}}><strong>Maharashtra Labour Welfare Board</strong></p>
                    <p style={{textAlign: 'center'}}>Shri. Bhaskar Morade</p>
                    <p style={{textAlign: 'center'}}>( Welfare Commissioner )</p>
                    
                    <h3>Board's Motto:</h3>
                    <p>Discipline, Service, Improvement and Prosperity.</p>
                    
                    <h3>Board's Goal:</h3>
                    <p>To enhance the standard of living of registered workers from various sectors of society in Maharashtra state by providing welfare schemes, programs and facilities, and to bring about their social, cultural, educational, economic or mental development through the board's welfare schemes.</p>
                    
                    <h3>Board's Functions:</h3>
                    <p>To provide welfare services for workers and their families.</p>
                    <ol>
                      <li>Excellent sports and recreational facilities</li>
                      <li>Modern library and reading room</li>
                      <li>Modern educational facilities</li>
                      <li>Organization of cultural and social programs</li>
                      <li>Employment and self-employment related programs</li>
                      <li>Health related programs</li>
                      <li>Other support services</li>
                    </ol>
                    
                    <h3>Quality Policy:</h3>
                    <p>To achieve the board's objectives through planned and systematic organization of welfare programs that provide comprehensive satisfaction to workers.</p>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Introduction Section */}
          <section className="overview-section" data-read-section="overview-introduction">
            <h2 className="section-title">
              {isMarathi() ? 'परिचय' : 'Introduction'}
            </h2>
            <div className="section-content">
              <div className="section-text">
                {isMarathi() ? (
                  <div>
                    <p style={{textAlign: 'center'}}><strong>महाराष्ट्र कामगार कल्याण मंडळ</strong></p>
                    
                    <p>मुंबई कामगार कल्याण निधी अधिनियम १९५३ नुसार कामगार कल्याण निधी व स्वायत्त मंडळाची स्थापना झाली. १ जुले १९५३ रोजी स्थापन झालेल्या "मुंबई कामगार कल्याण मंडळ " १ मे १९६० रोजी महाराष्ट्र राज्याच्या स्थापनेनंतर " महाराष्ट्र कामगार कल्याण मंडळ " असे नामांतर झाले.</p>
                    
                    <p>कामगार आणि त्यांच्या कुटुंबियांसाठी कामगार कल्याण योजना राबविणारे महाराष्ट्र हे देशातील अग्रगण्य राज्य आहे.</p>
                    
                    <p>तात्कालिन मुंबई राज्य शासनाने १९३८ साली मुंबई शहरामध्ये डिलाईल रोड (सध्याचा ना.म.जोशी मार्ग) येथे पहिले कामगार कल्याण केंद्र स्थापन करून बहुसंख्येने कामगार राहत असलेल्या वस्ती, गिरण्या, कारखाने या त्यांच्या कामाच्या ठिकाणी कल्याणकारी उपक्रम राबविण्यास सुरुवात केली. कामगारांना दारू, जुगार यासारख्या व्यसनांपासून परावृत्त करणे आणि त्यांचा फावला वेळ चांगल्या रितीने व्यतीत करता यावा. त्यांना आपला विकास साधण्याची संधी मिळावी हा यामागे शासनाचा हेतू होता. कामगार वसाहतीमध्ये वाचनालये, ग्रंथालये, आंतरगृह व मैदानी खेळ आणि अधूमधून करमणुकीचे कार्यक्रम आयोजित करता यावेत यासाठी शासनाने कामगार कल्याण केंद्र सुरू केली.</p>
                    
                    <p>१९४६ साली तात्कालीन मुंबई राज्य शासनाने घोषणा केली की, कामगारांच्या सर्वांगीण  उत्कर्षामागे फक्त शासनापुरतेच प्रयत्न मर्यादित न राखता कामगाराच्या संघटनांनी, मालकांनी व स्थानिक संस्थांनी हे कार्य पार पाडले पाहिजे. देशाची खरीखुरी प्रगती नागरिकांच्या स्वाभाविक आंतरिक तळमळीबरोबरच त्यांच्या संपन्न चारित्राचे विकास होणार आहे. सर्वांगीण  विकासासाठी शासनाने केलेले कायदे सहायभूत होऊ शकतील परंतु "स्वावलंबनाद्वारे विकास" याला दुसरा पर्याय नाही .</p>
                    
                    <p>तात्कालीन शासनाच्या कामगार कल्याणाच्या बदलत्या विचारांना पूरक असा नियोजनबद्ध व "एकत्रित कामगार कल्याण" चा विचार साकार करण्यासाठी कामगार कल्याण कायद्याची आखणी केली आणि मुंबई कामगार कल्याण निधी अधिनियम १९५३ अन्वये जून १९५३ मध्ये मुंबई कामगार कल्याण अधिनियमाची निर्मिती केली. सुरूवातीस सदर अधिनियम काही महत्वाच्या कामगार वासाहतीपुरताच मर्यादित होता.</p>
                    
                    <p>मुंबई कामगार कल्याण निधी अधिनियम १९५३ नुसार कामगार कल्याण निधी व स्वायत्त मंडळाची स्थापना झाली. तेव्हापासून या मंडळाद्वारे कामगार व त्यांच्या कुटुंबासाठी कामाच्या ठिकाणाबाहेर कल्याणकारी योजना राबविण्यास सुरुवात झाली. १ जुले १९५३ रोजी स्थापन झालेल्या "मुंबई कामगार कल्याण मंडळाचे महाराष्ट्र राज्याच्या स्थापनेनंतर "महाराष्ट्र कामगार कल्याण मंडळ" असे नामांतर झाले .</p>
                    
                    <p>जून १९५३ मध्ये मुंबई राज्य शासनाने मुंबई कामगार कल्याण निधी अधिनियम १९५३ अन्वये कायदा करून औद्योगिक कामगाराच्या विकासासाठी स्वतंत्र वैधानिक मंडळाची स्थापना केली. त्यानुसार शासनाकडून ३० जून १९५३ रोजी त्यांच्या अधिपत्याखाली असलेली ५४ कामगार कल्याण केंदे कर्मचा-यांसह  या मंडळाकडे सुपूर्द केली एकूण ५४ केंद्रापैकी २ केंद्रे ही म्हैसूर शासनाकडे १९५९ मध्ये तर १३, केंद्रे गुजरात शासनाकडे १९६० साली हस्तांतरित करण्यात आली. त्यानंतर १९६२ मध्ये विदर्भ व मराठवाडा विभागातील ३० केंद्राचा कार्यभार शासनाने या मंडळाकडे सोपविला .</p>
                    
                    <h3>पहिले वैधानिक (कामगार कल्याण मंडळ):</h3>
                    <p>शासनाने मुंबई  कामगार कल्याण निधी अधिनियम १९५३ हा कामगार कल्याण विषयक महत्वाचा कायदा जून १९५३ मध्ये मंजूर केला. त्या कायद्यांतर्गत कामगार कल्याण कार्यासाठी स्वतंत्र कल्याण निधी निर्माण करून कामगार कल्याणकारी कार्यक्रम पार पाडण्याची जबाबदारी कामगार, मालक आणि शासन यांच्या स्वायत्त मंडळाची म्हणजेच (त्यावेळच्या मुंबई कामगार कल्याण मंडळाकडे) आताच्या महाराष्ट्र कामगार कल्याण मंडळाकडे सोपविण्यात आली. शासनामार्फत मंडळासाठी कामगारांचे, मालकांचे, महिलांचे तसेच स्वतंत्र सदस्य नियुक्त करण्यात येतात. या मंडळावर २६ सदस्य असून त्यामध्ये अध्यक्षचा समावेश आहे. या सदस्यामध्ये ६ कामगार प्रतिनिधी, ६ मालक प्रतिनिधी, ६ महिला प्रतिनिधी आणि ८ स्वतंत्र प्रतिनिधींचा समावेश आहे. या मंडळाचा कालावधी ३ वर्षाचा असतो. ३ महिन्यातून कमीत कमी एकदा या मंडळाची बैठक होते. मंडळाचे अध्यक्ष या बैठकीत सूत्रसंचालन करतात. आता महाराष्ट्र शासनाने त्यांच्या उद्योग, ऊर्जा व कामगार विभागाच्या दि. ०७ जानेवारी २००२ च्या अध्यादेशानुसार शासनाने वित्त  विभागाचे प्रधान सचिव किंवा त्यांचे नियोजित प्रतिनिधी आणि कामगार विभागाचे प्रधान सचिव किंवा  त्यांचे नियोजित प्रतिनिधी यांची महाराष्ट्र कामगार कल्याण मंड़ळावर मुंबई कामगार कल्याण निधी अधिनियम १९५३ च्या कलम ४ च्या तरतुदिनुसार पदसिद्ध सदस्य म्हणून नियुक्ती केलेली आहे.</p>
                  </div>
                ) : (
                  <div>
                    <p style={{textAlign: 'center'}}><strong>Maharashtra Labour Welfare Board</strong></p>
                    
                    <p>The Labour Welfare Fund and autonomous board were established under the Mumbai Labour Welfare Fund Act 1953. The "Mumbai Labour Welfare Board" established on July 1, 1953, was renamed as "Maharashtra Labour Welfare Board" after the formation of Maharashtra State on May 1, 1960.</p>
                    
                    <p>Maharashtra is a leading state in the country for implementing labour welfare schemes for workers and their families.</p>
                    
                    <p>In 1938, the then Mumbai State Government established the first labour welfare centre at Delisle Road (present N.M. Joshi Marg) in Mumbai city and began implementing welfare programs in settlements, mills, and factories where a large number of workers resided. The government's intention was to divert workers from addictions like alcohol and gambling and help them spend their leisure time constructively, giving them opportunities for their development. The government started labour welfare centres so that libraries, reading rooms, indoor and outdoor games, and entertainment programs could be organized in labour settlements.</p>
                    
                    <p>In 1946, the then Mumbai State Government declared that for the overall development of workers, not only should the government's efforts be limited, but workers' organizations, employers, and local institutions should also carry out this work. The real progress of the country will come through the natural internal enthusiasm of citizens along with the development of their prosperous character. The laws made by the government for overall development can be supportive, but there is no alternative to "development through self-reliance."</p>
                    
                    <p>To realize the idea of planned and "integrated labour welfare" that complements the changing thoughts of labour welfare of the then government, labour welfare law was drafted and the Mumbai Labour Welfare Act was created in June 1953 under the Mumbai Labour Welfare Fund Act 1953. Initially, this act was limited to some important labour settlements only.</p>
                    
                    <p>Under the Mumbai Labour Welfare Fund Act 1953, the Labour Welfare Fund and autonomous board were established. Since then, this board has started implementing welfare schemes for workers and their families outside the workplace. The "Mumbai Labour Welfare Board" established on July 1, 1953, was renamed as "Maharashtra Labour Welfare Board" after the formation of Maharashtra State.</p>
                    
                    <p>In June 1953, the Mumbai State Government enacted a law under the Mumbai Labour Welfare Fund Act 1953 and established an independent statutory board for the development of industrial workers. Accordingly, on June 30, 1953, the government handed over 54 labour welfare centres under their jurisdiction along with employees to this board. Out of the total 54 centres, 2 centres were transferred to Mysore Government in 1959 and 13 centres were transferred to Gujarat Government in 1960. Later, in 1962, the government handed over the responsibility of 30 centres in Vidarbha and Marathwada regions to this board.</p>
                    
                    <h3>First Statutory (Labour Welfare Board):</h3>
                    <p>The government approved the Mumbai Labour Welfare Fund Act 1953, an important law related to labour welfare, in June 1953. Under this law, a separate welfare fund was created for labour welfare work, and the responsibility of implementing labour welfare programs was entrusted to the autonomous board of workers, employers, and government, i.e., (then Mumbai Labour Welfare Board) now Maharashtra Labour Welfare Board. The government appoints workers', employers', women's, and independent members for the board. This board has 26 members including the chairman. These members include 6 worker representatives, 6 employer representatives, 6 women representatives, and 8 independent representatives. The term of this board is 3 years. The board meeting is held at least once every 3 months. The chairman of the board conducts the proceedings of this meeting. Now, the Maharashtra Government has appointed the Principal Secretary of Finance Department or their designated representative and the Principal Secretary of Labour Department or their designated representative as ex-officio members on the Maharashtra Labour Welfare Board under the provisions of Section 4 of the Mumbai Labour Welfare Fund Act 1953 through their Industry, Energy and Labour Department's ordinance dated January 7, 2002.</p>
                  </div>
                )}
              </div>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default Overview;

